import { Box, CardActionArea, CardContent, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SmsIcon from '@mui/icons-material/Sms';
import React, { FC } from 'react';

import { AuthorLabel } from 'src/components/AuthorLabel';
import { CampaignPreset } from 'src/api/campaignPresets/types';
import { FavoriteStarIcon, LineClampTypography } from 'src/UIKit';
import { HoverActions, HoverActionsCard } from './HoverActionsCard';

interface PresetCardProps {
  preset: CampaignPreset;
  canArchive: boolean;
  canEdit: boolean;
  favorite: boolean;
  favoritePending?: boolean;
  icon?: React.ReactNode;
  onUse: (preset: CampaignPreset) => void;
  onArchive: (preset: CampaignPreset) => void;
  onEdit: (preset: CampaignPreset) => void;
  onFavorite: (preset: CampaignPreset) => void;
}

export const PresetCard: FC<PresetCardProps> = ({
  preset,
  canArchive,
  canEdit,
  favorite,
  favoritePending = false,
  icon,
  onUse,
  onArchive,
  onEdit,
  onFavorite,
}) => {
  const authorLabel = preset.authorIsCurrentUser ? 'You' : preset.authorName;

  const handleArchive = (event: React.MouseEvent) => {
    // Prevent the click from bubbling up to the card body (onUse).
    event.stopPropagation();
    onArchive(preset);
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit(preset);
  };

  const handleFavorite = (event: React.MouseEvent) => {
    event.stopPropagation();
    onFavorite(preset);
  };

  return (
    <HoverActionsCard variant="outlined">
      <CardActionArea onClick={() => onUse(preset)}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box display="flex" fontSize={22}>
              {icon ?? <SmsIcon fontSize="inherit" />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pr: 7 }}>
              <Box display="flex" alignItems="baseline" gap={0.5} mb={0.5} minWidth={0}>
                <Typography variant="subtitle1" noWrap minWidth={0} lineHeight={1.3}>
                  {preset.name}
                </Typography>
                {authorLabel && <AuthorLabel author={authorLabel} />}
              </Box>
              {preset.description?.trim() ? (
                <LineClampTypography variant="body2" color="text.secondary" lineHeight={1.3}>
                  {preset.description}
                </LineClampTypography>
              ) : null}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>

      {(canEdit || canArchive) && (
        <HoverActions>
          {canEdit && (
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" aria-label="Edit Template" onClick={handleEdit}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          {canArchive && (
            <Tooltip title="Delete">
              <IconButton size="small" color="primary" aria-label="Delete Template" onClick={handleArchive}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </HoverActions>
      )}

      <Tooltip title={favorite ? 'Remove from favorites' : 'Add to favorites'}>
        <Box component="span" position="absolute" top={0} bottom={0} right={8} zIndex={2} display="flex" alignItems="center">
          <IconButton
            size="small"
            aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
            disabled={favoritePending}
            onClick={handleFavorite}
          >
            <FavoriteStarIcon favorite={favorite} />
          </IconButton>
        </Box>
      </Tooltip>
    </HoverActionsCard>
  );
};
